import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight, Landmark } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { MaskedLine } from "@/components/Reveal";
import { IMG } from "@/constants/assets";

export function Hero() {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <img src={IMG.hero} alt="Château de Veauce" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0A08] via-[#0B0A08]/55 to-[#0B0A08]/30" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B0A08]/70 via-transparent to-transparent" />

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 1 }}
        className="hidden lg:flex absolute right-10 top-1/2 -translate-y-1/2 items-center gap-4 [writing-mode:vertical-rl]"
      >
        <span className="h-24 w-px bg-[#C5A059]/40" />
        <span className="font-mono-d text-[10px] tracking-[0.5em] text-[#B8B0A2] uppercase">Menuiserie d'Art · Restauration du Patrimoine</span>
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pb-24 md:pb-32 pt-40 w-full">
        <motion.div
          data-testid="hero-badge"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="inline-flex items-center gap-2 border hairline px-4 py-2 mb-8 bg-[#0B0A08]/40 backdrop-blur-sm"
        >
          <Landmark size={13} className="text-[#D4AF37]" />
          <span className="font-mono-d text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#F7F4EE]">{t("hero_badge")}</span>
        </motion.div>

        <h1 className="font-serif-d leading-[0.95] text-[#F7F4EE]">
          <MaskedLine delay={0.5} className="text-5xl sm:text-7xl lg:text-8xl">{t("hero_l1")}</MaskedLine>
          <MaskedLine delay={0.68} className="text-5xl sm:text-7xl lg:text-8xl">
            <em className="gold-text not-italic font-medium">{t("hero_l2")}</em>
          </MaskedLine>
          <MaskedLine delay={0.86} className="text-5xl sm:text-7xl lg:text-8xl">{t("hero_l3")}</MaskedLine>
        </h1>

        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-xl text-sm md:text-base text-[#B8B0A2] leading-relaxed"
        >
          {t("hero_sub")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a href="#expression" data-testid="hero-cta-expression-besoins"
            className="gold-btn inline-flex items-center gap-3 border border-[#D4AF37] px-8 py-4 text-[12px] tracking-[0.2em] uppercase text-[#D4AF37]">
            {t("hero_cta1")} <ArrowRight size={15} />
          </a>
          <a href="#realisations" data-testid="hero-cta-realisations"
            className="inline-flex items-center gap-3 px-8 py-4 text-[12px] tracking-[0.2em] uppercase text-[#F7F4EE] border border-[rgba(247,244,238,0.25)] hover:border-[#F7F4EE] transition-colors duration-300">
            {t("hero_cta2")}
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-[#7A7367]"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.div>
    </section>
  );
}
